// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>

import { closeButton, openOverlay } from '../overlay';
import { getBundle, loadDetail } from '../data';
import { duration, esc, shortDate } from '../format';
import type { Recall } from '../types';
import { setParams } from '../router';

/**
 * The recall dossier.
 *
 * Ordered the way a worried person reads: what is it, what could happen, what
 * do I do. The consumer-action field comes SECOND, above the supplier and the
 * category metadata, because it is the only part of the record that tells
 * somebody holding the product what to actually do with it.
 */
export function openRecall(recall: Recall): void {
  const { hazardById, termById } = getBundle();
  setParams({ r: recall.key });

  openOverlay({
    panelClass: 'drawer',
    label: `Recall: ${recall.title}`,
    lockScroll: true,
    onClose: () => setParams({ r: null }),
    build(panel, close) {
      const head = document.createElement('div');
      head.className = 'overlay-head';
      const h = document.createElement('h2');
      h.textContent = recall.title;
      head.append(h, closeButton(close, 'Close recall details'));

      const haz = hazardById.get(recall.hazard);
      const cats = recall.cats.map((c) => termById.get(c)?.name).filter(Boolean) as string[];

      const body = document.createElement('div');
      body.className = 'stack';
      body.innerHTML = `
        <div class="row" style="flex-wrap:wrap;gap:var(--space-sm)">
          <button type="button" class="pill glossary-link" data-term="hazard"
                  style="background:${haz?.colour ?? '#6b655b'};color:#fff;border-color:transparent;text-decoration:none"
                  aria-label="Hazard class ${esc(haz?.label ?? 'Unclassified')}, inferred by this site — what does that mean?"
          >${esc(haz?.label ?? 'Unclassified')}<span aria-hidden="true"> ⓘ</span></button>
          <span class="pill">${esc(shortDate(recall.date))}</span>
          ${recall.onSaleDays != null ? `<span class="pill">On sale ${esc(duration(recall.onSaleDays))} before recall</span>` : ''}
          ${recall.source === 'fsanz' ? '<span class="pill">Food recall — held by FSANZ</span>' : ''}
          ${recall.source === 'vehicle' ? '<span class="pill">Vehicle recall — also in the vehicle register</span>' : ''}
        </div>
        <div><div class="stat-label">Supplier</div><div>${esc(recall.supplier)}</div></div>
        <div id="detail-slot"><div class="skeleton" style="height:8rem"></div></div>
        ${cats.length ? `<div><div class="stat-label">Categories</div><div class="small">${cats.map((c) => `<span class="pill">${esc(c)}</span>`).join(' ')}</div></div>` : ''}
      `;
      panel.append(head, body);

      const slot = body.querySelector<HTMLElement>('#detail-slot')!;

      // Not every entry in the ACCC's list is an ACCC record. Food recalls are
      // handed to FSANZ and vehicle recalls to the Department of Infrastructure,
      // and in the food case the ACCC publishes no hazard detail at all. Saying
      // so — and linking to whoever does hold it — is the only useful thing this
      // panel can do; rendering an empty notice would read as a broken page.
      if (recall.source === 'fsanz') {
        slot.innerHTML = `
          <div class="note">
            <strong>This is a food recall, and its details are published by Food Standards
            Australia New Zealand.</strong>
            <p style="margin-top:.4rem">The ACCC lists it here, but the description of what is wrong and
            what to do lives with FSANZ. This site does not copy it, because the ACCC's own page for this
            recall redirects there rather than publishing the detail.</p>
          </div>
          <p class="small"><a href="https://www.foodstandards.gov.au/consumer/safety/recalls" target="_blank" rel="noopener">Australian food recall alerts, at FSANZ ↗</a></p>`;
        return;
      }

      void loadDetail(recall.key).then((d) => {
        if (!d) {
          slot.innerHTML = '<div class="note">The full notice for this recall could not be loaded. Follow the link to the official register below.</div>';
          return;
        }
        const loc: string[] = [];
        if (d.location.national) loc.push('Nationally');
        if (d.location.channels.length) loc.push(...d.location.channels);
        if (d.location.states.length) loc.push(d.location.states.join(', '));
        if (d.location.unresolved.length) loc.push(...d.location.unresolved);

        slot.innerHTML = `
          ${d.productDescription ? section('What the product is', d.productDescription) : ''}
          ${d.defect ? section('What is wrong with it', d.defect) : ''}
          ${d.hazard ? section('What could happen', d.hazard) : ''}
          ${d.consumerAction ? `<div class="note"><strong>What to do</strong><div style="margin-top:.35rem;white-space:pre-line">${esc(d.consumerAction)}</div></div>` : ''}
          ${d.traders ? section('Where it was sold', d.traders) : ''}
          ${loc.length ? kv('Sold', loc.join(' · ')) : ''}
          ${d.saleRaw ? kv('On sale between', d.saleRaw) : ''}
          ${d.country ? kv('Made in', d.country) : ''}
          ${d.pra ? kv('PRA number', d.pra) : ''}
          <p class="small"><a href="https://www.productsafety.gov.au${esc(d.url)}" target="_blank" rel="noopener">Read the official recall notice ↗</a></p>
          <div class="note small">This page is a copy of the register, not the register itself. Always check the
          official notice before acting, and contact the supplier if you are unsure whether your item is affected.</div>
        `;
      }).catch(() => {
        slot.innerHTML = '<div class="note">The full notice could not be loaded just now.</div>';
      });
    },
  });
}

function section(title: string, text: string): string {
  return `<div style="margin-bottom:var(--space-md)">
    <div class="stat-label">${esc(title)}</div>
    <div style="white-space:pre-line">${esc(text)}</div>
  </div>`;
}

function kv(k: string, val: string): string {
  return `<div class="row small" style="gap:var(--space-sm)">
    <span class="muted shrink0">${esc(k)}:</span><span class="grow">${esc(val)}</span></div>`;
}
